import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import './FileUpload.css'

interface FileUploadProps {
  onFileUploaded: (file: UploadedFile) => void
  onFileRemoved: (fileId: string) => void
  uploadedFiles: UploadedFile[]
  accept?: Record<string, string[]>
  maxFiles?: number
  maxSize?: number
}

interface UploadedFile {
  id: string
  name: string
  url: string
  type: 'image' | 'video' | 'document'
  size: number
  description?: string
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileUploaded,
  onFileRemoved,
  uploadedFiles,
  accept = {
    'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    'video/*': ['.mp4', '.webm', '.ogg', '.avi', '.mov'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
  },
  maxFiles = 10,
  maxSize = 10 * 1024 * 1024 // 10MB
}) => {
  const [uploading, setUploading] = useState<string[]>([])
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({})

  const uploadToS3 = useCallback(async (file: File): Promise<string> => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://54v0v1tgji.execute-api.eu-west-2.amazonaws.com/prod'
    const API_KEY = process.env.REACT_APP_API_KEY || 'FzPL9CynuW6eta7tjLw9G1QtIItNW34V9XLO4ZmG'
    
    try {
      // Step 1: Get presigned upload URL from lambda
      const uploadUrlResponse = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
        },
        body: JSON.stringify({
          action: 'getUploadUrl',
          fileName: file.name,
          fileType: file.type
        })
      })

      if (!uploadUrlResponse.ok) {
        throw new Error('Failed to get upload URL')
      }

      const uploadData = await uploadUrlResponse.json()
      
      if (!uploadData.success) {
        throw new Error(uploadData.error || 'Failed to get upload URL')
      }

      // Step 2: Upload file to S3 using presigned URL
      const uploadResponse = await fetch(uploadData.data.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        }
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3')
      }

      // Return the public URL
      return uploadData.data.publicUrl
    } catch (error) {
      console.error('S3 upload error:', error)
      throw new Error('Failed to upload file to S3')
    }
  }, [])

  const getFileType = (file: File): 'image' | 'video' | 'document' => {
    if (file.type.startsWith('image/')) return 'image'
    if (file.type.startsWith('video/')) return 'video'
    return 'document'
  }

  const getFileIcon = (type: 'image' | 'video' | 'document') => {
    switch (type) {
      case 'image': return '🖼️'
      case 'video': return '🎥'
      case 'document': return '📄'
      default: return '📁'
    }
  }

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    for (const file of acceptedFiles) {
      const fileId = `${Date.now()}-${file.name}`
      setUploading(prev => [...prev, fileId])
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }))

      try {
        // Simulate upload progress
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: Math.min((prev[fileId] || 0) + Math.random() * 30, 90)
          }))
        }, 500)

        const url = await uploadToS3(file)
        
        clearInterval(progressInterval)
        setUploadProgress(prev => ({ ...prev, [fileId]: 100 }))

        const uploadedFile: UploadedFile = {
          id: fileId,
          name: file.name,
          url,
          type: getFileType(file),
          size: file.size,
        }

        onFileUploaded(uploadedFile)
        
        setTimeout(() => {
          setUploading(prev => prev.filter(id => id !== fileId))
          setUploadProgress(prev => {
            const newProgress = { ...prev }
            delete newProgress[fileId]
            return newProgress
          })
        }, 1000)

      } catch (error) {
        console.error('Upload failed:', error)
        setUploading(prev => prev.filter(id => id !== fileId))
        setUploadProgress(prev => {
          const newProgress = { ...prev }
          delete newProgress[fileId]
          return newProgress
        })
        
        // Show more specific error message
        const errorMessage = error instanceof Error ? error.message : 'Upload failed. Please try again.'
        alert(`Upload failed: ${errorMessage}`)
      }
    }
  }, [onFileUploaded, uploadToS3])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles: maxFiles - uploadedFiles.length,
    maxSize,
    disabled: uploadedFiles.length >= maxFiles
  })

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="file-upload-component">
      {uploadedFiles.length < maxFiles && (
        <div
          {...getRootProps()}
          className={`file-upload-area ${isDragActive ? 'drag-over' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="file-upload-icon">📎</div>
          <div className="file-upload-text">
            {isDragActive ? (
              <p>Drop the files here...</p>
            ) : (
              <p>Drag & drop files here, or click to select files</p>
            )}
          </div>
          <small>
            Max {maxFiles} files, up to {formatFileSize(maxSize)} each
          </small>
        </div>
      )}

      {/* Upload Progress */}
      {uploading.length > 0 && (
        <div className="upload-progress">
          {uploading.map(fileId => {
            const progress = uploadProgress[fileId] || 0;
            return (
              <div key={fileId} className="upload-progress-item">
                <div className="upload-progress-info">
                  <span>Uploading...</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="upload-progress-bar">
                  <div 
                    className={`upload-progress-fill progress-${Math.round(progress / 10) * 10}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="uploaded-files">
          <h4>Uploaded Files ({uploadedFiles.length})</h4>
          {uploadedFiles.map(file => (
            <div key={file.id} className="uploaded-file">
              <div className="uploaded-file-info">
                <span className="file-icon">{getFileIcon(file.type)}</span>
                <div>
                  <div className="file-name">{file.name}</div>
                  <div className="file-details">
                    {formatFileSize(file.size)} • {file.type}
                  </div>
                </div>
              </div>
              <button
                className="uploaded-file-remove"
                onClick={() => onFileRemoved(file.id)}
                title="Remove file"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default FileUpload
